/**
 * Typed Google Tag Manager dataLayer adapter.
 *
 * GTM loads from `index.html` once the runtime public config resolves the
 * container id (see `public-config.ts`; skipped when no id). App code never
 * touches `window.dataLayer` directly — it goes through `pushEvent` (semantic
 * events for GTM-managed tags like GA4), the consent helpers (GTM Consent
 * Mode), and the exclusion-signal helpers (`analytics_excluded`, for tags
 * like the GA4 Configuration tag that GTM fires natively and that
 * `pushEvent` never touches). PostHog is handled separately and
 * directly; see `analytics.ts`.
 */

import { analyticsUrl, sanitizeAnalyticsProps } from './analytics-url'
import {
  safeStorageGet,
  safeStorageRemove,
  safeStorageSet,
} from './safe-storage'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

type ConsentState = 'granted' | 'denied'

/** The Consent Mode signals we drive from the unified consent banner. */
type ConsentSignals = {
  ad_storage: ConsentState
  analytics_storage: ConsentState
  ad_user_data: ConsentState
  ad_personalization: ConsentState
}

function ensureDataLayer(): unknown[] | null {
  if (typeof window === 'undefined') return null
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

// Also hand-copied as a string literal in index.html's inline script (no
// import mechanism available there) — keep the two in sync if this changes.
const ANALYTICS_EXCLUDED_KEY = 'stopal.analytics-excluded'
const ANALYTICS_MANUAL_OPTOUT_KEY = 'stopal.analytics-manual-optout'

/**
 * Mirrors the exclusion flag onto `dataLayer` as `analytics_excluded`, so GTM
 * can gate tags off an ordinary Data Layer Variable — the container-side
 * backstop for whatever `pushEvent()`'s client-side chokepoint can't reach
 * (a tag re-scoped to Initialization/All Pages in the GTM GUI, or a
 * `pushEvent` bug; see docs/analytics.md "GTM-side backstop").
 * The initial value for a given page load is pushed inline in
 * `index.html`, before gtm.js loads; this keeps it current after that for
 * sign-in-driven changes within the same session.
 */
function pushExclusionState(excluded: boolean): void {
  const dl = ensureDataLayer()
  if (!dl) return
  dl.push({ analytics_excluded: excluded })
}

/**
 * Manual `?stopal_notrack=1` opt-out — sticky, and takes precedence over
 * sign-in-based updates (see `setAnalyticsExcluded`) so a later
 * non-excluded sign-in on this device can't silently undo it.
 */
export function setAnalyticsManualOptOut(): void {
  if (typeof window === 'undefined') return
  safeStorageSet(ANALYTICS_MANUAL_OPTOUT_KEY, '1')
  safeStorageSet(ANALYTICS_EXCLUDED_KEY, '1')
  pushExclusionState(true)
}

/**
 * Persist (or clear) the GA4 exclusion flag for the signed-in user, so the
 * device stays excluded across reloads. The `excluded` value is the effective
 * per-user analytics-exclusion flag resolved server-side (see
 * `resolveAnalyticsExcluded` in `@stopal/shared`) — it covers internal staff,
 * contractors, and demo / App Store review accounts, not a hardcoded list.
 * No-ops if a manual opt-out (see `setAnalyticsManualOptOut`) is active on
 * this device — that always wins over sign-in state.
 */
export function setAnalyticsExcluded(excluded: boolean): void {
  if (typeof window === 'undefined') return
  if (safeStorageGet(ANALYTICS_MANUAL_OPTOUT_KEY) === '1') return
  if (excluded) {
    safeStorageSet(ANALYTICS_EXCLUDED_KEY, '1')
  } else {
    safeStorageRemove(ANALYTICS_EXCLUDED_KEY)
  }
  pushExclusionState(excluded)
}

function isAnalyticsExcluded(): boolean {
  if (typeof window === 'undefined') return false
  return safeStorageGet(ANALYTICS_EXCLUDED_KEY) === '1'
}

/**
 * Push a semantic event to GTM as dataLayer object-form, which the container's
 * custom-event triggers pick up. That is the only delivery path that reaches
 * GA4: the per-event *GA4 Event* tags (docs/analytics.md).
 *
 * The trailing `window.gtag('event', …)` call is NOT a second delivery path.
 * `window.gtag` is the stub defined in `index.html`
 * (`function gtag(...a){dataLayer.push(a)}`), and a GA4 destination deployed
 * *through GTM* does not consume those array-form pushes as GA4 events —
 * verified empirically: GA4 records zero custom events over a month while
 * every GA4 Event tag is present and firing correctly for consenting traffic.
 * Kept only because removing it is a behaviour change that should land on its
 * own; it is otherwise inert and must not be treated as a fallback.
 *
 * No-ops entirely for devices flagged as excluded via `setAnalyticsExcluded`
 * or `setAnalyticsManualOptOut`. This covers every custom event pushed
 * through here — including `page_view`, which the mandated container config
 * fires the GA4 Configuration tag from (docs/analytics.md). The
 * `analytics_excluded` dataLayer variable (pushed by `pushExclusionState`
 * and the inline script in `index.html`) is the GTM-side backstop for
 * container misconfiguration, not the primary gate.
 * PostHog is untouched.
 */
export function pushEvent(
  event: string,
  props?: Record<string, unknown>,
): void {
  if (isAnalyticsExcluded()) return
  const dl = ensureDataLayer()
  if (!dl) return
  const safe = props
    ? sanitizeNamedUrlKeys(sanitizeAnalyticsProps(props))
    : props
  dl.push({ event, ...safe })
  // `page_location` is sent explicitly rather than left to GA4, which otherwise
  // reads it from `document.location` — a page reached from an email carries a
  // live single-use credential in its query string.
  window.gtag?.('event', event, {
    ...(safe ?? {}),
    page_location: analyticsUrl(window.location.href),
  })
}

/**
 * GA4's reserved URL parameters. Sanitised BY NAME and unconditionally, on top
 * of the shape-driven pass — for a key known to be a URL there is no ambiguity,
 * so a host-relative value (`stopal.app/join?token=…`, which is not URL-shaped
 * and must not be, or every sentence becomes a URL) is still stripped. This
 * mirrors the named pass PostHog keeps for the SDK's own URL properties; the
 * shape rule replaced the key list as the FLOOR, not as a narrowing.
 */
const GA4_URL_KEYS = ['page_path', 'page_location', 'page_referrer']

/**
 * Copies rather than mutating, so it is safe in EITHER composition order. It is
 * currently fed `sanitizeAnalyticsProps`'s fresh copy, which made an in-place
 * version safe by accident — and the natural future edit ("the named pass is
 * the floor, run it first") would then have written into the caller's own
 * payload, the same object `analytics.ts` hands to PostHog a microtask later.
 */
function sanitizeNamedUrlKeys(
  props: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...props }
  for (const key of GA4_URL_KEYS) {
    const value = out[key]
    if (typeof value === 'string') out[key] = analyticsUrl(value)
  }
  return out
}

/** The two independently-gated groups behind the consent banner. */
export type ConsentDecision = {
  /** First-party analytics (GA4 + PostHog). Granted whenever the user accepts. */
  analytics: boolean
  /**
   * Advertising / remarketing signals (`ad_*`). On native iOS these are granted
   * only when App Tracking Transparency is authorized — see `att.ts`.
   */
  ads: boolean
}

function toState(granted: boolean): ConsentState {
  return granted ? 'granted' : 'denied'
}

function consentSignals(decision: ConsentDecision): ConsentSignals {
  const ads = toState(decision.ads)
  return {
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
    analytics_storage: toState(decision.analytics),
  }
}

/**
 * Update GTM Consent Mode after the user makes a choice. The *default*
 * (denied) is set inline in `index.html` before `gtm.js` runs — this only
 * pushes the post-decision update.
 *
 * `analytics` and `ads` are set independently: on native iOS advertising
 * signals stay `denied` unless ATT is authorized, while first-party analytics
 * still follows the cookie banner. On web/Android both track the same choice.
 *
 * Consent commands are pushed as an arguments-style array (`['consent',
 * 'update', …]`), which is how GTM's Consent Mode reads them — distinct from
 * the `{ event, … }` shape used by `pushEvent`.
 */
export function updateConsent(decision: ConsentDecision): void {
  const dl = ensureDataLayer()
  if (!dl) return
  dl.push(['consent', 'update', consentSignals(decision)])
}
